import { createSlice } from "@reduxjs/toolkit";
import User from "redux/classes/User";
import Model from "redux/classes/Model";
import Job from "redux/classes/Job";
import Config from "redux/classes/Config";
import QA from "redux/classes/QA";
import Output from "redux/classes/Output";
import { FormInstance } from "antd";

interface initialStateTypes {
  userID: string;
  users: User[];
  models: Model[];
  modelIndex: number;
  jobs: Job[];
  chatUrl: string;
  jobIndex: number;
  config: Config;
  qa: QA[];
  output: Output[];
  outputIndex: number;
  visualIndex: number;
  htmlIndex: number;
  sheetIndex: number;
  files: {[key: string]: string[]};
  flow: string;
  forms: FormInstance[];
  jobPanelsComplete?: {[key: string]: boolean};
  settings?: { [key: string]: any };
  // Admin "view as" mode. When adminViewUserID is non-empty, the console is
  // rendering another user's POV — read-only, no mutations. adminViewModalOpen
  // controls the picker overlay (kept in redux so any header — Layout / NLI /
  // Dash — can dispatch open without prop drilling).
  adminViewUserID: string;
  adminViewModalOpen: boolean;
}

const initialState: initialStateTypes = {
  userID: "",
  users: [],
  models: [],
  modelIndex: -1,
  jobs: [],
  chatUrl: "",
  jobIndex: -1,
  config: new Config({}, {}, {}, {}),
  qa: [],
  output: [],
  outputIndex: 0,
  visualIndex: 0,
  htmlIndex: 0,
  sheetIndex: 0,
  files: {},
  flow: "",
  forms: [],
  jobPanelsComplete: undefined,
  // Preseed settings.theme from the last cached theme name so antd's
  // ConfigProvider algorithm and our useThemeColors hook resolve to the
  // user's saved theme on the very first render — the API-backed settings
  // hydrate moments later. We don't preseed any other settings field.
  settings: (() => {
    try {
      const lastThemeName = localStorage.getItem("velocity:lastThemeName");
      if (lastThemeName) return { theme: lastThemeName };
    } catch {
      // private/incognito — ignore
    }
    return undefined;
  })(),
  // Preseed adminViewUserID from localStorage so a page reload doesn't
  // unexpectedly drop the admin out of "view as" mode. Cleared on signOut.
  // The modal-open flag is ephemeral and should NOT persist.
  adminViewUserID: (() => {
    try {
      return localStorage.getItem("velocity:adminViewUserID") || "";
    } catch {
      return "";
    }
  })(),
  adminViewModalOpen: false,
};

export const userReducer = createSlice({
  name: "user",
  initialState,
  reducers: {
    signOut: (state) => {
      state.userID = "";
      state.users = [];
      state.models = [];
      state.modelIndex = -1;
      state.jobs = [];
      state.chatUrl = "";
      state.jobIndex = -1;
      state.config = new Config({}, {}, {}, {});
      state.qa = [];
      state.output = [];
      state.outputIndex = 0;
      state.visualIndex = 0;
      state.htmlIndex = 0;
      state.sheetIndex = 0;
      state.files = {};
      state.flow = "";
      state.forms = [];
      state.jobPanelsComplete = undefined;
      state.settings = undefined;
      state.adminViewUserID = "";
      state.adminViewModalOpen = false;
      try {
        localStorage.removeItem("velocity:adminViewUserID");
      } catch {
        // private/incognito — ignore
      }
    },

    setUserID: (state, { payload }) => {
      state.userID = payload.userID;
    },

    setUsers: (state, { payload }) => {
      state.users = payload.users;
    },

    setModels: (state, { payload }) => {
      state.models = payload.models;
    },

    setJobs: (state, { payload }) => {
      state.jobs = payload.jobs;
    },

    mergeJobStates: (state, { payload }) => {
      // Light polling-merge for one model's slice. payload.states is the
      // result of tableGrabber's loadJobStates Query — `[{job_id, model_id,
      // job_status, result_name, end_time}]`. For each existing job in this
      // model's slice, update its status/result_name/end_time fields. Drop
      // any existing job whose ID isn't in the response (deletion detection).
      // Brand-new IDs (in response but not in state) are intentionally NOT
      // added here — the action layer fires a follow-up LoadJobsByIDsAction
      // to fetch their full field set so they can render.
      const modelID: string = payload.modelID;
      const states: Array<{
        job_id: string;
        job_status?: string;
        result_name?: string;
        end_time?: string;
      }> = payload.states || [];
      const stateMap = new Map(states.map(s => [s.job_id, s]));

      state.jobs = state.jobs.flatMap((job: Job) => {
        if (job.modelID !== modelID) return [job]; // other models untouched
        const u = stateMap.get(job.jobID);
        if (!u) return []; // dropped — not in light Query response → deleted
        return [new Job(
          job.jobID,
          job.modelID,
          job.custID,
          job.userID,
          job.alias,
          job.desc,
          job.jobName,
          u.result_name !== undefined ? u.result_name : job.resultName,
          job.taskID,
          u.job_status !== undefined ? u.job_status : job.status,
          job.startTime,
          u.end_time ? new Date(u.end_time) : job.endTime,
          job.scheduleTime,
          job.scheduleType,
        )];
      });
    },

    mergeJobsByIDs: (state, { payload }) => {
      // Add freshly-fetched full Job records to state, replacing any with
      // matching IDs. Used after the light-poll Query surfaces new IDs and
      // the follow-up loadJobsByIDs returns their full field set.
      const newJobs: Job[] = payload.jobs;
      const newIDs = new Set(newJobs.map(j => j.jobID));
      state.jobs = state.jobs.filter(j => !newIDs.has(j.jobID)).concat(newJobs);
    },

    setChatUrl: (state, { payload }) => {
      state.chatUrl = payload.chatUrl;
    },

    setQA: (state, { payload }) => {
      state.qa = payload.qa;
    },

    setConfig: (state, { payload }) => {
      if (payload.reset) {
        state.config = new Config({}, {}, {}, {});
      }
      else {
        state.config = new Config(
          payload.meta,
          payload.parameters,
          payload.inputs,
          payload.outputs
        );
      }
    },

    modifyConfig: (state, { payload }) => {
      // state.config is a Config class instance, and immer treats class
      // instances as leaves — it doesn't proxy them, so mutations like
      // `state.config.parameters = X` are NOT tracked and state.config never
      // gets a new identity. That's why the Run page's "X of Y configured"
      // counter only updated on Run Model click (which dispatches a different
      // action whose state mutation IS tracked). Fix: replace state.config
      // wholesale with a new Config instance per dispatch.
      const next = new Config(
        payload.type === "meta"       ? { ...payload.value } : state.config.meta,
        payload.type === "parameters" ? { ...payload.value } : state.config.parameters,
        payload.type === "inputs"     ? { ...payload.value } : state.config.inputs,
        payload.type === "outputs"    ? { ...payload.value } : state.config.outputs,
      );
      state.config = next;
    },

    setOutput: (state, { payload }) => {
      state.output = payload.output.filter((output: Output) => output.result !== '');
    },

    setOutputIndex: (state, { payload }) => {
      state.outputIndex = payload.index;
    },

    setVisualIndex: (state, { payload }) => {
      state.visualIndex = payload.index;
    },

    setHtmlIndex: (state, { payload }) => {
      state.htmlIndex = payload.index;
    },

    setSheetIndex: (state, { payload }) => {
      state.sheetIndex = payload.index;
    },

    setFiles: (state, { payload }) => {
      state.files = payload.files;
    },

    setModelIndex: (state, { payload }) => {
      for (const model of state.models) {
        if (model.modelID === payload.modelID) {
          state.modelIndex = state.models.indexOf(model)
        }
      }
    },

    setJobIndex: (state, { payload }) => {
      if (state.jobs.length === 0) {
        state.jobIndex = -1
      }
      else if (payload.index === -1) {
        state.jobIndex = -1
      }
      else {
        for (const job of state.jobs) {
          if (job.jobID === payload.jobID) {
            state.jobIndex = state.jobs.indexOf(job)
          }
        }
      }
    },

    setFlow: (state, { payload }) => {
      state.flow = payload.flow
    },

    setForms: (state, { payload }) => {
      state.forms = payload.forms
    },

    addForm: (state, { payload }) => {
      state.forms.push(payload.form)
    },

    setJobPanelsComplete: (state, { payload }) => {
      state.jobPanelsComplete = payload.jobPanelsComplete
    },

    updateJobPanelsComplete: (state, { payload }) => {
      if (state.jobPanelsComplete) {
        state.jobPanelsComplete![payload.panel] = payload.complete
      }
    },

    setSettings: (state, { payload }) => {
      state.settings = payload.settings;
    },

    setAdminViewUserID: (state, { payload }) => {
      const next = payload.adminViewUserID || "";
      state.adminViewUserID = next;
      try {
        if (next) {
          localStorage.setItem("velocity:adminViewUserID", next);
        } else {
          localStorage.removeItem("velocity:adminViewUserID");
        }
      } catch {
        // private/incognito — ignore
      }
    },

    setAdminViewModalOpen: (state, { payload }) => {
      state.adminViewModalOpen = !!payload.open;
    },
  }
});

export const {
  signOut,
  setUserID,
  setUsers,
  setModels,
  setJobs,
  mergeJobStates,
  mergeJobsByIDs,
  setChatUrl,
  setQA,
  setConfig,
  modifyConfig,
  setOutput,
  setOutputIndex,
  setVisualIndex,
  setHtmlIndex,
  setSheetIndex,
  setFiles,
  setModelIndex,
  setJobIndex,
  setFlow,
  setForms,
  addForm,
  setJobPanelsComplete,
  updateJobPanelsComplete,
  setSettings,
  setAdminViewUserID,
  setAdminViewModalOpen,
} = userReducer.actions;

export default userReducer.reducer;
